<template>
  <v-card class="bi-parameter-detail-card pa-3" outlined>
    <div class="bi-parameter-detail-header font-weight-bold ui-text-md">
      {{ $t('biControlPanel.parameterRules.wizard.parametersSectionTitle') }}
    </div>
    <div class="d-flex align-center flex-wrap mb-2">
      <div class="text--secondary">
        {{ $t('biControlPanel.parameterRules.wizard.parametersSectionDescription') }}
      </div>
      <v-spacer />
      <v-btn v-if="canAddParameter" text color="primary" @click="addParameter">
        <v-icon left size="18">mdi-plus-circle-outline</v-icon>
        {{ $t('biControlPanel.parameterRules.wizard.addParameter') }}
      </v-btn>
    </div>

    <div
      v-for="item in parameters"
      :key="item.tempId"
      class="bi-parameter-row d-flex align-center flex-wrap pa-2 mb-2"
    >
      <div class="d-flex align-center flex-wrap bi-parameter-row-fields">
        <div class="bi-parameter-row-field">
          <Autocomplete
            :value="item.parameterType"
            :items="availableParameterTypeOptions(item)"
            item-text="label"
            item-value="code"
            :label="$t('biControlPanel.parameterRules.wizard.parameterType')"
            :rules="[rule.required]"
            :disabled="!item.isNew"
            hide-details
            v-bind="autocompleteAttrs"
            @change="setParameterType(item.tempId, $event)"
          />
        </div>

        <div v-if="item.parameterType" class="bi-parameter-row-field">
          <Autocomplete
            v-if="item.parameterType === 'LIFE_COUNT'"
            :value="item.lifeCountRule"
            :items="lifeCountRuleOptions"
            item-text="label"
            item-value="code"
            :label="$t('biControlPanel.parameterRules.wizard.parameterValue')"
            :rules="[rule.required]"
            hide-details
            v-bind="autocompleteAttrs"
            @change="setParameterValue(item.tempId, 'lifeCountRule', $event)"
          />
          <Autocomplete
            v-else-if="item.parameterType === 'COMPETENCY'"
            :value="item.competencyType"
            :items="competencyTypeOptions"
            item-text="label"
            item-value="code"
            :label="$t('biControlPanel.parameterRules.wizard.parameterValue')"
            :rules="[rule.required]"
            hide-details
            v-bind="autocompleteAttrs"
            @change="setParameterValue(item.tempId, 'competencyType', $event)"
          />
        </div>
      </div>

      <v-spacer />

      <v-switch
        :input-value="item.active"
        hide-details
        class="mt-0 pt-0 bi-parameter-row-switch"
        :label="item.active
          ? $t('biControlPanel.parameterRules.table.active')
          : $t('biControlPanel.parameterRules.table.inactive')"
        @change="setActive(item.tempId, $event)"
      />

      <v-tooltip v-if="item.isNew && parameters.length > 1" bottom>
        <template #activator="{ on, attrs }">
          <v-icon
            color="error"
            size="18"
            class="ml-3"
            v-bind="attrs"
            v-on="on"
            @click="removeParameter(item.tempId)"
          >
            mdi-delete-outline
          </v-icon>
        </template>
        <span>{{ $t('common.remove') }}</span>
      </v-tooltip>
    </div>
  </v-card>
</template>

<script>
import Autocomplete from '@/components/Default/Autocomplete.vue';
import { AUTOCOMPLETE_ATTRS } from '@/shared/constants/admissionMap/DefaultConstants';
import BiPanelRuleModule from '@/store/modules/bi-panel-rule/bi-panel-rule-module';
import Rules from '@/shared/validators/formRules';
import {
  BI_PARAMETER_TYPE_OPTIONS,
  BI_LIFE_COUNT_RULE_OPTIONS,
  BI_COMPETENCY_TYPE_OPTIONS,
  defaultCompetencyType,
} from '@/store/modules/bi-panel-rule/bi-panel-rule-factory';

export default {
  name: 'ParameterDetailStep',
  components: { Autocomplete },
  data() {
    return {
      lifeCountRuleOptions: BI_LIFE_COUNT_RULE_OPTIONS,
      competencyTypeOptions: BI_COMPETENCY_TYPE_OPTIONS,
      rule: new Rules(),
    };
  },
  computed: {
    parameters() {
      return BiPanelRuleModule.form.parameters;
    },
    canAddParameter() {
      return BiPanelRuleModule.canAddFormParameter;
    },
    autocompleteAttrs() {
      return {
        ...AUTOCOMPLETE_ATTRS,
        borderSelected: false,
        placeholder: this.$t('biControlPanel.filters.placeholder').toString(),
      };
    },
  },
  methods: {
    availableParameterTypeOptions(item) {
      const usedTypes = new Set(
        this.parameters
          .filter((parameter) => parameter.tempId !== item.tempId && parameter.parameterType)
          .map((parameter) => parameter.parameterType),
      );

      return BI_PARAMETER_TYPE_OPTIONS.filter((option) => !usedTypes.has(option.code));
    },
    addParameter() {
      BiPanelRuleModule.addFormParameter();
    },
    removeParameter(tempId) {
      BiPanelRuleModule.removeFormParameter(tempId);
    },
    setParameterType(tempId, code) {
      const competencyType = code === 'COMPETENCY'
        ? defaultCompetencyType(BiPanelRuleModule.form.paymentModels)
        : null;

      BiPanelRuleModule.updateFormParameter({
        tempId,
        patch: {
          parameterType: code || null,
          lifeCountRule: null,
          competencyType,
          competencyIsDefault: !!competencyType,
        },
      });
    },
    setParameterValue(tempId, field, value) {
      BiPanelRuleModule.updateFormParameter({
        tempId,
        patch: { [field]: value || null, competencyIsDefault: false },
      });
    },
    setActive(tempId, value) {
      BiPanelRuleModule.updateFormParameter({ tempId, patch: { active: !!value } });
    },
  },
};
</script>

<style lang="scss" scoped>
.bi-parameter-detail-card {
  border-radius: 8px;
}

.bi-parameter-detail-header {
  border-bottom: 2px solid var(--v-primary-base);
  display: inline-block;
  padding-bottom: 4px;
  margin-bottom: 4px;
}

.bi-parameter-row {
  border: 1px solid var(--v-border-base, #e0e0e0);
  border-radius: 6px;
}

.bi-parameter-row-fields {
  gap: 12px;
}

.bi-parameter-row-field {
  min-width: 200px;
  max-width: 260px;
}

.bi-parameter-row-switch {
  max-width: 140px;
}
</style>
